/**
 * createPrefetchHook — companion factory for codegen-emitted detail hooks.
 *
 * Returns a hook that yields a stable `(args) => Promise<void>` callback
 * suitable for `onMouseEnter` / `onFocus` / route-intent prefetching. The
 * callback fires the real HTTP request and writes the response into the
 * SAME cache entry the paired `useGet*` reader observes — calling it does
 * NOT subscribe the caller; the component owning the callback never
 * re-renders when the data lands, and no NATS subscription is opened.
 *
 * Composition mirrors `createHook`: the factory takes a `HookSpec` (the
 * codegen-emitted spec carrying the queryKey factory + real queryFn) and
 * routes the call through TanStack's `queryClient.prefetchQuery`. App code
 * never touches `useQueryClient` directly — the SDK owns that surface.
 *
 * Emitted for every `useGet*` query (detail and list) by the codegen
 * template — every reader has a prefetch companion, mechanical mirror.
 * The SDK does not gate which queries are "worth" prefetching; that's
 * the frontend's call.
 *
 * The returned callable also exposes `prepare(args)`, which describes the
 * exact cache operation without starting it. Schedulers can use the opaque
 * task key for priority/deduplication and can safely detach or abort through
 * the task without owning a second HTTP client. Task bookkeeping is plain
 * process state and never subscribes a React component to the query cache.
 *
 * Usage (codegen-emitted):
 *
 *     function MarketCard({ pubkey }: { pubkey: string }) {
 *       const prefetchMarket = usePrefetchGetMarket();
 *       return (
 *         <a
 *           href={`/market/${pubkey}`}
 *           onMouseEnter={() => prefetchMarket({ pubkey })}
 *           onFocus={() => prefetchMarket({ pubkey })}
 *         >…</a>
 *       );
 *     }
 *
 * Docs alignment:
 *   - https://tanstack.com/query/latest/docs/framework/react/guides/prefetching
 */
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSdkViewerScope } from '../../provider/SdkRuntimeContext.js';
import { resolveEntityQueryKey } from '../internal/scoped-entity-store.js';
const sharedRunsByClient = new WeakMap();
export function createPrefetchHook(spec) {
  const factory = spec.entityKeys[spec.kind];
  if (factory === undefined) {
    throw new Error(`createPrefetchHook: spec.entityKeys.${spec.kind} is undefined for ${spec.specName} ` + '(codegen invariant violation — every emitted HookSpec MUST populate the ' + 'factory matching its `kind`).');
  }
  return function usePrefetch() {
    const queryClient = useQueryClient();
    const viewerScope = useSdkViewerScope();
    return useMemo(() => {
      const prepare = args => {
        const opts = factory(args);
        const entityQueryKey = resolveEntityQueryKey(spec, args, viewerScope);
        const entityId = String(args.pubkey ?? opts.queryKey[opts.queryKey.length - 1] ?? '');
        const queryKey = spec.canonicalEntity === true && entityQueryKey !== undefined && entityId.length > 0 ? entityQueryKey(entityId) : opts.queryKey;
        const queryOptions = {
          queryKey,
          queryFn: opts.queryFn,
          ...(opts.staleTime !== undefined ? {
            staleTime: opts.staleTime
          } : {}),
          ...(opts.meta === undefined ? {} : {
            meta: opts.meta
          })
        };
        const key = queryClient.defaultQueryOptions(queryOptions).queryHash;
        return createPrefetchTask(queryClient, key, queryOptions);
      };
      return Object.assign(args => prepare(args).run(), {
        prepare
      });
    }, [queryClient, viewerScope]);
  };
}
function createPrefetchTask(queryClient, key, queryOptions) {
  const owner = Symbol(key);
  let state = 'prepared';
  let sharedRun;
  let taskPromise;
  const run = () => {
    if (state === 'cancelled' || state === 'settled') return Promise.resolve();
    if (taskPromise !== undefined) return taskPromise;
    state = 'running';
    const runs = getSharedRuns(queryClient);
    const existing = runs.get(key);
    let activeRun;
    if (existing !== undefined) {
      existing.owners.add(owner);
      activeRun = existing;
    } else {
      const queryBefore = queryClient.getQueryCache().get(key);
      const wasRunning = queryBefore !== undefined && queryBefore.state.fetchStatus !== 'idle';
      const promise = queryClient.prefetchQuery(queryOptions);
      const queryAfter = queryClient.getQueryCache().get(key);
      activeRun = {
        owners: new Set([owner]),
        promise,
        sdkInitiated: !wasRunning && queryAfter?.state.fetchStatus !== 'idle'
      };
      runs.set(key, activeRun);
      const registeredRun = activeRun;
      void promise.finally(() => {
        if (runs.get(key) === registeredRun) runs.delete(key);
        registeredRun.owners.clear();
      });
    }
    sharedRun = activeRun;
    taskPromise = activeRun.promise.then(() => {
      if (state === 'running') state = 'settled';
    });
    return taskPromise;
  };
  const cancel = async () => {
    if (state === 'prepared') {
      state = 'cancelled';
      return 'dropped';
    }
    if (state === 'cancelled' || state === 'settled' || sharedRun === undefined) {
      return 'settled';
    }
    state = 'cancelled';
    sharedRun.owners.delete(owner);
    if (sharedRun.owners.size > 0) return 'retained';
    const query = queryClient.getQueryCache().get(key);
    if (query === undefined || query.state.fetchStatus === 'idle') return 'settled';
    if (!sharedRun.sdkInitiated || query.getObserversCount() > 0) return 'retained';
    const runs = getSharedRuns(queryClient);
    if (runs.get(key) === sharedRun) runs.delete(key);
    await queryClient.cancelQueries({
      queryKey: queryOptions.queryKey,
      exact: true
    }, {
      revert: true,
      silent: true
    });
    return 'aborted';
  };
  return {
    key,
    run,
    cancel
  };
}
function getSharedRuns(queryClient) {
  const existing = sharedRunsByClient.get(queryClient);
  if (existing !== undefined) return existing;
  const runs = new Map();
  sharedRunsByClient.set(queryClient, runs);
  return runs;
}